import { useMemo } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import {
  ArrowRightLeft,
  FileSignature,
  LayoutDashboard,
  Contact,
  Settings2,
  ShieldAlert,
  UsersRound,
} from 'lucide-react';
import { ErrorState } from '@/components/common/ErrorState';
import { PageShell } from '@/components/common/PageShell';
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar';
import {
  SegmentedControl,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui';
import { usePermissions } from '@/hooks/usePermissions';
import { classifyError } from '@/lib/errorKind';
import {
  WorkspaceCompanyChips,
  useWorkspaceCompanyFilter,
  type WorkspaceCompanyFilter,
} from '@/features/companies/components/WorkspaceCompanyFilter';
import { CompanyMovementsTab } from '@/features/companies/components/CompanyMovementsTab';
import { upgradeLegacyHrParams, type HrArea } from '../hrAreas';
import { HrCasesTab } from './cases/HrCasesTab';
import { HrDocumentsTab } from './documents/HrDocumentsTab';
import { HrAreaHeader, type HrAreaProps } from './hrShared';
import { HrOverviewTab, type HrGoTo } from './overview/HrOverviewTab';
import { OrgChartPanel } from './people/OrgChartPanel';
import { PeopleList } from './people/PeopleList';
import { HrPoliciesTab } from './policies/HrPoliciesTab';
import { ProbationTab } from './ProbationTab';
import { HrSetupTab } from './setup/HrSetupTab';

type HrAction =
  | 'employment'
  | 'directory'
  | 'movements'
  | 'probation'
  | 'documents'
  | 'policies'
  | 'discipline'
  | 'grievances'
  | 'structure';

/** An area shows when the user holds any of its actions, and each screen re-checks its own. */
const AREAS: Array<{
  value: HrArea;
  label: string;
  actions: HrAction[];
  icon: React.ComponentType<{ className?: string }>;
}> = [
  {
    value: 'overview',
    label: 'Overview',
    actions: [
      'employment',
      'directory',
      'movements',
      'probation',
      'documents',
      'policies',
      'discipline',
      'grievances',
      'structure',
    ],
    icon: LayoutDashboard,
  },
  { value: 'people', label: 'People', actions: ['employment'], icon: UsersRound },
  { value: 'changes', label: 'Job changes', actions: ['movements', 'probation'], icon: ArrowRightLeft },
  { value: 'letters', label: 'Letters & policies', actions: ['documents', 'policies'], icon: FileSignature },
  { value: 'cases', label: 'Cases', actions: ['discipline', 'grievances'], icon: ShieldAlert },
  {
    value: 'setup',
    label: 'Setup',
    actions: ['structure', 'documents', 'probation', 'discipline', 'grievances'],
    icon: Settings2,
  },
];

const TRIGGER_CLASS =
  'shrink-0 rounded-md px-3 py-1.5 text-sm transition-colors duration-150 hover:bg-gray-50 aria-selected:bg-primary/10 aria-selected:text-primary dark:hover:bg-gray-700/50 dark:aria-selected:text-sky-400';

/** Breadcrumb is rendered by the global Header (`pageBreadcrumb.ts`); this stays for PageShell. */
const CRUMBS = [{ label: 'Workspace', to: '/dashboard' }, { label: 'People & HR' }];

/** A control that switches between the screens inside one area. */
function useAreaView<T extends string>(
  options: Array<{ value: T; allowed: boolean }>
): [T | undefined, (value: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const allowed = options.filter((option) => option.allowed);
  const requested = searchParams.get('view');
  const view = allowed.find((option) => option.value === requested)?.value ?? allowed[0]?.value;
  return [
    view,
    (value) => {
      const next = new URLSearchParams();
      for (const key of ['tab', 'companyId']) {
        const current = searchParams.get(key);
        if (current) next.set(key, current);
      }
      next.set('view', value);
      setSearchParams(next, { replace: true });
    },
  ];
}

function PeopleArea({
  companyIds,
  showCompany,
  filter,
}: HrAreaProps & { filter: WorkspaceCompanyFilter }): React.ReactElement {
  const [view, setView] = useAreaView<'list' | 'chart'>([
    { value: 'list', allowed: true },
    { value: 'chart', allowed: true },
  ]);
  const chartCompanies = companyIds?.length
    ? filter.companies.filter((company) => companyIds.includes(company._id))
    : filter.companies;
  return (
    <div className="space-y-4">
      <HrAreaHeader
        title="People"
        description="Everyone across your companies. Open a person for their job, history, documents and leave."
        controls={
          <SegmentedControl
            ariaLabel="People view"
            value={view ?? 'list'}
            onChange={setView}
            options={[
              { value: 'list', label: 'List' },
              { value: 'chart', label: 'Org chart' },
            ]}
          />
        }
      />
      {view === 'chart' ? (
        <OrgChartPanel companies={chartCompanies} />
      ) : (
        <PeopleList
          companyIds={companyIds}
          showCompany={showCompany}
          companyNameById={filter.companyNameById}
          onApplyCompany={(companyId) => {
            filter.reset();
            if (companyId) filter.toggle(companyId);
          }}
        />
      )}
    </div>
  );
}

function ChangesArea(props: HrAreaProps): React.ReactElement {
  const { can } = usePermissions();
  const canMove = can('hr', 'movements');
  const canProbation = can('hr', 'probation');
  const [view, setView] = useAreaView<'changes' | 'probation'>([
    { value: 'changes', allowed: canMove },
    { value: 'probation', allowed: canProbation },
  ]);
  return (
    <div className="space-y-4">
      <HrAreaHeader
        title={view === 'probation' ? 'Probation' : 'Job changes'}
        description={
          view === 'probation'
            ? 'Confirm, extend or end each probation before it lapses.'
            : "Move, promote or change the pay of someone. Each change is approved and dated, so the history stays accurate."
        }
        controls={
          canMove && canProbation ? (
            <SegmentedControl
              ariaLabel="Job changes view"
              value={view ?? 'changes'}
              onChange={setView}
              options={[
                { value: 'changes', label: 'Job changes' },
                { value: 'probation', label: 'Probation' },
              ]}
            />
          ) : undefined
        }
      />
      {view === 'probation' ? <ProbationTab {...props} /> : <CompanyMovementsTab {...props} />}
    </div>
  );
}

function LettersArea({
  companies,
  ...props
}: HrAreaProps & { companies: WorkspaceCompanyFilter['companies'] }): React.ReactElement {
  const { can } = usePermissions();
  const canLetters = can('hr', 'documents');
  const canPolicies = can('hr', 'policies');
  const [view, setView] = useAreaView<'letters' | 'policies'>([
    { value: 'letters', allowed: canLetters },
    { value: 'policies', allowed: canPolicies },
  ]);
  return (
    <div className="space-y-4">
      <HrAreaHeader
        title={view === 'policies' ? 'Policies' : 'Letters'}
        description={
          view === 'policies'
            ? 'Publish policies and handbooks, and see who has acknowledged them.'
            : 'Generate offer, confirmation and other letters from templates, then send them for signature.'
        }
        controls={
          canLetters && canPolicies ? (
            <SegmentedControl
              ariaLabel="Letters and policies view"
              value={view ?? 'letters'}
              onChange={setView}
              options={[
                { value: 'letters', label: 'Letters' },
                { value: 'policies', label: 'Policies' },
              ]}
            />
          ) : undefined
        }
      />
      {view === 'policies' ? (
        <HrPoliciesTab {...props} companies={companies} />
      ) : (
        <HrDocumentsTab {...props} />
      )}
    </div>
  );
}

function HrWorkspace(): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const { can, isLoading: permissionsLoading } = usePermissions();
  const filter = useWorkspaceCompanyFilter();
  const { companies, companiesLoading, companiesError, companyIds } = filter;

  const allowedAreas = useMemo(
    () => AREAS.filter((area) => area.actions.some((action) => can('hr', action))),
    [can]
  );
  const requestedArea = searchParams.get('tab');
  const activeArea: HrArea | null =
    allowedAreas.find((area) => area.value === requestedArea)?.value ??
    allowedAreas[0]?.value ??
    null;

  /** Moving between areas drops the last area's filters but keeps the company filter. */
  const goTo: HrGoTo = (area, params = {}) => {
    const next = new URLSearchParams();
    const company = searchParams.get('companyId');
    if (company) next.set('companyId', company);
    next.set('tab', area);
    for (const [key, value] of Object.entries(params)) next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const inView = companyIds?.length ?? companies.length;
  const areaProps: HrAreaProps = { companyIds, showCompany: inView > 1 };

  let body: React.ReactNode;
  if (companiesLoading || permissionsLoading) {
    body = (
      <div className="flex items-center justify-center py-12" role="status">
        <Spinner />
        <span className="sr-only">Loading People & HR</span>
      </div>
    );
  } else if (companiesError) {
    // A forbidden company-list response needs an access explanation rather than
    // exposing an API error to the user.
    const forbidden = classifyError(companiesError).kind === 'forbidden';
    body = (
      <ErrorState
        error={companiesError}
        size="section"
        {...(forbidden && {
          title: "Your role can't list companies",
          description:
            'Ask an administrator to check your company membership and HR permissions, then sign in again.',
        })}
      />
    );
  } else if (companies.length === 0) {
    body = (
      <HrEmpty
        title="No companies yet"
        message="Once you belong to a company, its people appear here."
      />
    );
  } else if (!activeArea) {
    body = (
      <HrEmpty
        title="No HR screens available"
        message="Your role does not include any HR access. An organization admin can grant it."
      />
    );
  } else {
    body = (
      <Tabs value={activeArea} onValueChange={(value) => goTo(value as HrArea)}>
        <TabsList className="flex w-auto max-w-full gap-1 overflow-x-auto rounded-[var(--radius-panel)] border border-gray-200 bg-white p-1 shadow-[var(--shadow-panel)] sm:inline-flex dark:border-gray-700/80 dark:bg-gray-800 dark:shadow-none">
          {allowedAreas.map(({ value, label, icon: Icon }) => (
            <TabsTrigger key={value} value={value} className={TRIGGER_CLASS}>
              <span className="flex items-center gap-2 whitespace-nowrap">
                <Icon className="h-4 w-4" aria-hidden />
                {label}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value={activeArea} className="mt-5 space-y-4">
          {activeArea === 'overview' && (
            <HrOverviewTab
              companyIds={companyIds}
              allowedAreas={allowedAreas.map((area) => area.value)}
              goTo={goTo}
            />
          )}
          {activeArea === 'people' && <PeopleArea {...areaProps} filter={filter} />}
          {activeArea === 'changes' && <ChangesArea {...areaProps} />}
          {activeArea === 'letters' && <LettersArea {...areaProps} companies={companies} />}
          {activeArea === 'cases' && (
            <div className="space-y-4">
              <HrAreaHeader
                title="Cases"
                description="The formal routes for when something goes wrong at work. Each case has its own team, a confidential record, and set time limits."
              />
              <HrCasesTab {...areaProps} />
            </div>
          )}
          {activeArea === 'setup' && <HrSetupTab companyIds={companyIds} companies={companies} />}
        </TabsContent>
      </Tabs>
    );
  }

  return (
    <PageShell
      breadcrumbItems={CRUMBS}
      icon={Contact}
      title="People & HR"
      className="gap-0"
      hideHeader
    >
      {companies.length > 1 && <WorkspaceToolbar filter={<WorkspaceCompanyChips filter={filter} />} />}
      <div className="mx-auto mt-5 w-full min-w-0 max-w-[1500px]">{body}</div>
    </PageShell>
  );
}

/**
 * People & HR: what needs doing across every company the user can reach, then
 * the areas that do it. Old links are upgraded before the company filter
 * mounts, because the filter reads its starting selection from the URL once.
 */
export function HrWorkspacePage(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const upgraded = upgradeLegacyHrParams(searchParams);
  if (upgraded) return <Navigate to={{ search: upgraded }} replace />;
  return <HrWorkspace />;
}

function HrEmpty({ title, message }: { title: string; message: string }): React.ReactElement {
  return (
    <div className="rounded-[var(--radius-panel)] border border-dashed border-gray-300 px-6 py-12 text-center dark:border-gray-700">
      <Contact className="mx-auto h-8 w-8 text-gray-400" aria-hidden />
      <h2 className="mt-3 text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
      <p className="mx-auto mt-1 max-w-md text-sm text-gray-600 dark:text-gray-300">{message}</p>
    </div>
  );
}
